import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {compose} from 'redux';
import Image from 'common/image/Image';
import isUndefined from 'lodash/isUndefined';
import TextButton from '@spothero/ui/text-button';
import {push} from 'router/router-actions';
import {
    showChangeDateTime,
    toggleTimeExtendedDetails,
} from 'checkout/checkout-actions';
import CheckoutUtils from 'checkout/checkout-utils';
import {SPOT_IMAGES} from 'spot-details/spot-details-prop-types';
import Disclaimers from './Disclaimers';
import ReviewPurchase from './ReviewPurchase';
import Card from 'common/card/Card';
import withConfig from 'config/withConfig';
import SHLoader from 'common/loader/SHLoader';

class ReservationSummary extends Component {
    static propTypes = {
        isMobile: PropTypes.bool.isRequired,
        starts: PropTypes.string.isRequired,
        ends: PropTypes.string,
        isAirport: PropTypes.bool.isRequired,
        isMonthly: PropTypes.bool.isRequired,
        selectedRate: PropTypes.object.isRequired,
        transientAvailable: PropTypes.bool.isRequired,
        monthlyAvailable: PropTypes.bool.isRequired,
        isLoadingRates: PropTypes.bool.isRequired,
        onChangeDateTimeClick: PropTypes.func.isRequired,
        onTimeExtendedClick: PropTypes.func.isRequired,
        facilityTitle: PropTypes.string.isRequired,
        facilityUrl: PropTypes.string.isRequired,
        spotImages: SPOT_IMAGES,
        staticBaseUrl: PropTypes.string,
        pushTo: PropTypes.func.isRequired,
    };
    _imageSize = 64;

    _onReservationTermClick = () => {
        const {isMonthly, starts, facilityUrl, pushTo} = this.props;
        const params = {
            starts,
            ...(!isMonthly && {monthly: true}),
        };

        pushTo(CheckoutUtils.buildURLFromParams(facilityUrl, params));
    };

    _getTransientTimes = () => {
        const {starts, ends, selectedRate} = this.props;
        const {starts: transientStarts, ends: transientEnds} = selectedRate;

        return CheckoutUtils.getTransientReservationInfo({
            reservationStarts: starts,
            reservationEnds: ends,
            rateStarts: transientStarts,
            rateEnds: transientEnds,
            format: 'ddd, MMM D, h:mm A',
        });
    };

    _getMonthlyTimes = () => {
        const {selectedRate, starts} = this.props;
        const {
            startDateFormatted: startDateTimeFormatted,
            endDateFormatted: endDateTimeFormatted,
        } = CheckoutUtils.getMonthlyReservationInfo(selectedRate, starts);

        return {
            startDateTimeFormatted,
            endDateTimeFormatted,
            timeExtended: false,
        };
    };

    render() {
        const {
            isMobile,
            isAirport,
            isMonthly,
            onChangeDateTimeClick,
            onTimeExtendedClick,
            transientAvailable,
            monthlyAvailable,
            facilityTitle,
            spotImages,
            staticBaseUrl,
            isLoadingRates,
        } = this.props;
        const {
            startDateTimeFormatted,
            endDateTimeFormatted,
            timeExtended,
        } = isMonthly ? this._getMonthlyTimes() : this._getTransientTimes();
        const labels = {
            monthly: ['Month Begins', 'Month Ends'],
            transient: ['Enter After', 'Exit Before'],
            airport: ['Parking Begins', 'Parking Ends'],
        };
        const labelKey = isAirport
            ? 'airport'
            : isMonthly
            ? 'monthly'
            : 'transient';
        const [startsLabel, endsLabel] = labels[labelKey];
        const imgProps = {
            className: 'ReservationSummary-collapsed-thumbnail',
            width: this._imageSize,
            height: this._imageSize,
            alt: `Photo of ${facilityTitle}`,
            ...(!isUndefined(spotImages[0])
                ? {cloudinaryTemplate: spotImages[0].url_template}
                : {src: `${staticBaseUrl}img/missing-image.png`}),
        };

        return (
            <div className="ReservationSummary-container">
                <div className="ReservationSummary">
                    {!isMobile && (
                        <Card className="ReservationSummary-facility-summary">
                            <div className="ReservationSummary-facility-summary-left">
                                <Image {...imgProps} />
                            </div>
                            <div className="ReservationSummary-facility-summary-right">
                                <span className="ReservationSummary-facility-title">
                                    {facilityTitle}
                                </span>
                            </div>
                        </Card>
                    )}
                    <Card
                        title={
                            <>
                                <h2>Reservation Summary</h2>
                                <TextButton
                                    className="ReservationSummary-edit-times"
                                    onClick={onChangeDateTimeClick}
                                >
                                    Edit
                                </TextButton>
                            </>
                        }
                    >
                        {isLoadingRates && <SHLoader />}
                        {!isLoadingRates && (
                            <>
                                {isMobile ? (
                                    <div className="ReservationSummary-collapsed">
                                        <div className="ReservationSummary-collapsed-left">
                                            <Image {...imgProps} />
                                        </div>
                                        <div className="ReservationSummary-collapsed-right">
                                            <span className="ReservationSummary-collapsed-title">
                                                {facilityTitle}
                                            </span>
                                            {isMonthly ? (
                                                <span className="ReservationSummary-collapsed-time">
                                                    {startDateTimeFormatted} -{' '}
                                                    {endDateTimeFormatted}
                                                </span>
                                            ) : (
                                                <>
                                                    <span className="ReservationSummary-collapsed-time">
                                                        {startDateTimeFormatted}
                                                    </span>
                                                    <span className="ReservationSummary-collapsed-time">
                                                        {endDateTimeFormatted}
                                                    </span>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                ) : (
                                    <div className="ReservationSummary-datetimes">
                                        <p data-test="start-date-time">
                                            <span className="label">
                                                {startsLabel}
                                            </span>
                                            {startDateTimeFormatted}
                                            <span className="arrow">
                                                &#x02192;
                                            </span>
                                        </p>
                                        <p data-test="end-date-time">
                                            <span className="label">
                                                {endsLabel}
                                            </span>
                                            {endDateTimeFormatted}
                                        </p>
                                    </div>
                                )}
                                {((isMonthly && transientAvailable) ||
                                    (!isMonthly && monthlyAvailable)) && (
                                    <TextButton
                                        className="toggle-monthly-parking"
                                        onClick={this._onReservationTermClick}
                                    >
                                        View {isMonthly ? 'Hourly' : 'Monthly'}{' '}
                                        Parking Options
                                    </TextButton>
                                )}
                                {timeExtended && (
                                    <TextButton
                                        className="ReservationSummary-time-extended-toggle"
                                        onClick={onTimeExtendedClick}
                                    >
                                        Why is my time extended for free?
                                    </TextButton>
                                )}
                                <ReviewPurchase />
                            </>
                        )}
                        <Disclaimers />
                    </Card>
                </div>
            </div>
        );
    }
}

const mapStateToProps = state => {
    const {
        searchRequest: {monthly: isMonthly, airport: isAirport, starts, ends},
        spot: {
            selected: {
                selectedRate,
                hourly_rates: hourlyRates,
                monthly_rates: monthlyRates,
                facility: {
                    title: facilityTitle,
                    images: spotImages,
                    transient_available: transientAvailable,
                    monthly_available: monthlyAvailable,
                    facility_url: facilityUrl,
                },
            },
        },
    } = state;

    return {
        isAirport,
        isMonthly,
        selectedRate,
        starts,
        ends,
        facilityTitle,
        transientAvailable,
        monthlyAvailable,
        facilityUrl,
        hourlyRates,
        monthlyRates,
        spotImages,
    };
};

const mapDispatchToProps = {
    onChangeDateTimeClick: showChangeDateTime,
    onTimeExtendedClick: toggleTimeExtendedDetails,
    pushTo: push,
};

const enhance = compose(
    withConfig(['isMobile', 'staticBaseUrl']),
    connect(mapStateToProps, mapDispatchToProps)
);

export default enhance(ReservationSummary);
